// 開幕紅幕 — Unity Slot026 的 `ani_loading` Spine。
//
// ⚠️ 資產命名極易誤導：`ani_loading` 不是 loading 頁面的動畫，而是「按下 loading 頁 Continue 之後、
//    在主遊戲畫面最上層播的紅色劇院布幕拉開演出」。（loading 頁本身的美術在 public/images/loading/。）
//
// Unity 第一手來源：
//   節點  Slot026_RedRidingHood/Prefab/UI/LandscapeUI.prefab → LandscapeUI/TopCanvas/Loading
//         （m_IsActive:0 預設關閉；父層 TopCanvas m_OverrideSorting:1、m_SortingOrder:40＝蓋在一切之上）
//   播放  Script/View/GamePlayUI.cs:401-407 PlayOpening()：
//           SetActive(true) → AnimationState.SetAnimation(0, _openingAnimation, false)
//           → await Delay(entryTrack.Animation.Duration) → SetActive(false)
//   觸發  Script/SlotStateMachine.cs:123-132 OnUILoadingContinueClickEvent（SubscriberPriority.Low）：
//           se_open → await PlayOpening() → mu_main_background → StartWolfIdle()/StartCharacterIdle()
//         同一事件的 High 優先訂閱者 LoadingUIMediator 早一步 Destroy 了 loading UI，所以 Unity 的觀感是
//         「loading 瞬間消失、已合攏的布幕接手蓋住畫面」，中間**沒有**露出遊戲畫面的空窗——本移植照此
//         （見 main.ts：先 showClosed() 蓋住，才移除 loading）。
//   資產  Art/Animaton/ani_loading/（skel 8953B、atlas 單頁 pma:true、唯一 region redCurtain_R
//         bounds 2,2,422,610）。動畫**只有一支** `ani_loading`、時長 1.0s、無 event timeline
//         （音效純由 C# 呼叫，故本檔不從骨骼觸發音效）。內容：`all` 骨 scale 3 之下左右兩片
//         weighted mesh 布幕，右片 translate x 0→+430.7943、左片 0→−428.7627（皆 cubic-bezier(1/3,0,2/3,1)
//         的對稱 ease-in-out），50 根 Fold 骨於 t=0/0.333/0.667/1.0 做 0→−2.231°→+2.920°→0 的褶皺擺盪，
//         兩個 slot 的 RGBA 於 t=0.8667→1.0 由 alpha 255→0 —— **布幕自己會淡出，故本檔不另加淡出**。
//
// 座標系推導（不是目測）：SkeletonGraphic referenceSize=(2524.939, 1830.0063)＝skeleton bounds 的尺寸、
//   layoutScale=1 → 1 skeleton unit = 1 UI(設計) px；RectTransform sizeDelta 同 referenceSize、
//   anchoredPosition (0,0)、pivot (0.49978992, 0.30983883)，而 pivot × sizeDelta = (1261.94, 567.00)
//   ＝ −bounds.(x, y) → **skeleton 原點正好落在 Canvas 中央**。故本檔＝置中 ＋ Unity Expand 等比縮放。

import { Container } from 'pixi.js'
import type { Spine } from '@esotericsoftware/spine-pixi-v8'
import {
  DESIGN,
  unityScaleLandscape,
  unityScalePortrait,
  type Orientation,
  type Resizable,
} from '../../framework/render/design'
import { delay } from '../../framework/core/async'
import { animationDuration, createEffectSpine, EFFECT_ANIM, hasAnimation } from '../assets/EffectSpines'

/**
 * skeleton 原點到 bounds 四邊的距離（Spine 4.2 二進位解析值：
 * bounds x=−1261.9393、y=−567.0017、w=2524.9395、h=1830.0009）。
 * 布幕主體偏上（向上 1263、向下僅 567），故「蓋滿畫面」的下限由較短的一側決定。
 */
const BOUNDS_HALF_X = 1261.9393
const BOUNDS_HALF_Y = 567.0017

/** 骨骼查不到動畫長度時的備援秒數（Unity 實測 ani_loading 為 1.0s）。 */
const OPENING_FALLBACK_S = 1

/**
 * 布幕縮放係數。基準是 Unity Canvas 的 Expand 係數（橫版 1920×1080／直版 1080×1920），
 * 另加「至少蓋滿畫面」的下限：布幕自 skeleton 原點向下只延伸 567 設計 px，畫面半高超過它就會露縫。
 *
 * [刻意偏離 Unity] Unity 直版根本不播這段演出——PoriraitUI.prefab 內沒有 GamePlayUI 元件，
 *   場景 Slot026_RedRidingHood.unity 的 GamePlayUIMediator._portraitUI 指向 PoriraitUI 內已不存在的
 *   fileID（失效參照），於是 InvokeAllUIsAsync(PlayOpening) 只跑到橫版。判定為來源疏漏而非設計意圖，
 *   本移植兩個方向都播；動畫只有一支、無 L/P 變體，直版沿用同一支並靠此下限確保蓋滿
 *   （直版半高 960 ≫ 567，不放大會在布幕下緣露出遊戲畫面）。
 */
export function curtainScale(width: number, height: number, orientation: Orientation): number {
  const unity =
    orientation === 'portrait' ? unityScalePortrait(width, height) : unityScaleLandscape(width, height)
  const coverX = width / (2 * BOUNDS_HALF_X)
  const coverY = height / (2 * BOUNDS_HALF_Y)
  return Math.max(unity, coverX, coverY)
}

export class OpeningCurtainView extends Container implements Resizable {
  /** 布幕骨骼；資產缺失時為 null（呼叫端退回 loading 淡出）。 */
  private readonly spine: Spine | null
  private screenW = DESIGN.landscape.width
  private screenH = DESIGN.landscape.height
  private orientation: Orientation = 'landscape'

  constructor() {
    super()
    // Unity 的 Loading 節點只掛 SkeletonGraphic、沒有 Button：演出期間不吃點擊（但仍實體遮住畫面）。
    this.eventMode = 'none'
    this.visible = false

    const spine = createEffectSpine('opening')
    if (spine !== null && hasAnimation(spine, EFFECT_ANIM.opening)) {
      // 先停在第一格（布幕合攏）；playOpen() 才交回 ticker 前進。
      spine.autoUpdate = false
      this.spine = spine
      this.addChild(spine)
    } else {
      if (spine !== null && !spine.destroyed) spine.destroy()
      this.spine = null
    }
  }

  resize(width: number, height: number, orientation: Orientation): void {
    if (this.destroyed || width === 0 || height === 0) return
    this.screenW = width
    this.screenH = height
    this.orientation = orientation
    this.applyTransform()
  }

  private applyTransform(): void {
    const spine = this.spine
    if (spine === null || spine.destroyed) return
    spine.scale.set(curtainScale(this.screenW, this.screenH, this.orientation))
    spine.position.set(this.screenW / 2, this.screenH / 2)
  }

  /**
   * 以動畫第一格（布幕合攏）蓋住畫面。回傳骨骼是否可用——呼叫端據此決定是直接移除 loading（Unity 行為）
   * 還是退回 loading 自身的淡出。
   */
  showClosed(): boolean {
    const spine = this.spine
    if (spine === null) return false
    this.applyTransform()
    spine.state.setAnimation(0, EFFECT_ANIM.opening, false)
    spine.update(0) // autoUpdate=false，需手動套用 t=0 才會擺出合攏姿勢
    this.visible = true
    return true
  }

  /** 播放拉開演出並等到播完（末段 0.1333s 的淡出由骨骼自帶）。骨骼不可用時立即返回。 */
  async playOpen(): Promise<void> {
    const spine = this.spine
    if (spine === null) return
    spine.autoUpdate = true
    await delay(animationDuration(spine, EFFECT_ANIM.opening, OPENING_FALLBACK_S) * 1000)
    this.visible = false
  }
}
